import React, { useState, useEffect } from "react";
import ReactDOM from 'react-dom';
import NewShipmentModal from "./NewShipmentModal";
import { fetchAllShipments } from "../../services/DonationService";
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { SearchInput } from '../../components/Search';
import { formatDate } from '../../utils/dateFormatter';

interface DonationAssignModalProps {
    open: boolean;
    onClose: () => void;
    donations?: any[];
}

const formatCurrency = (amount: number) =>
    amount.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 0 });

const DonationAssignModal: React.FC<DonationAssignModalProps> = ({
    open,
    onClose,
    donations = [],
}) => {
    const [showNewShipmentModal, setShowNewShipmentModal] = useState(false);
    const [shipments, setShipments] = useState<any[]>([]);

    // Pagination state for shipments
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5;
    const totalPages = Math.ceil(shipments.length / pageSize);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (open) {
            fetchAllShipments().then(res => {
                if (res && res.data) setShipments(res.data);
            });
        }
    }, [open]);

    if (!open) return null;

    // Aggregate donation data for summary
    const totalBars = donations.reduce((sum, d) => sum + (d.numberOfUnits || 0), 0);
    const totalBoxes = donations.reduce((sum, d) => sum + (d.numberOfBoxes || 0), 0);
    const totalAmount = donations.reduce((sum, d) => sum + (d.amount || 0), 0);
    const campaign =
        donations.length === 1
            ? donations[0]?.campaign?.name || "N/A"
            : donations.length > 1
                ? donations[0]?.campaign?.name || "N/A"
                : "-";

    // Handle new shipment creation
    const handleNewShipment = (shipment: any) => {
        setShipments([
            {
                createAt: shipment.createAt || new Date().toISOString(),
                name: shipment.name,
                address: shipment.address,
                orderedBoxUnit: shipment.orderedBoxUnit,
                currentStutus: shipment.currentStutus || "PENDING",
            },
            ...shipments,
        ]);
        onClose()
    };

    // Filter shipments by name
    const filteredShipments = shipments.filter(
        (shipment) =>
            shipment.name &&
            shipment.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Paginate filtered shipments
    const paginatedFilteredShipments = filteredShipments.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-lg w-full max-w-3xl">
                {/* Header */}
                <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Assign ({donations.length}) donations to shipment
                    </h2>
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-3xl"
                    >
                        &times;
                    </Button>
                </div>
                {/* Donations summary */}
                <div className="px-8 pt-6 pb-2">
                    <div className="grid grid-cols-4 gap-4 text-sm mb-2">
                        <div>
                            <div className="text-gray-500 dark:text-gray-400 mb-1">No. of donations</div>
                            <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donations.length}</div>
                        </div>
                        <div>
                            <div className="text-gray-500 dark:text-gray-400 mb-1">Bars (Boxes)</div>
                            <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                {totalBars} ({totalBoxes || 25})
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-500 dark:text-gray-400 mb-1">Amount</div>
                            <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                {formatCurrency(totalAmount)}
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-500 dark:text-gray-400 mb-1">Campaign</div>
                            <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{campaign}</div>
                        </div>
                    </div>
                </div>
                {/* Section divider */}
                <div className="border-t dark:border-white/10 mt-2 mb-0"></div>
                {/* Select shipment */}
                <div className="px-8 py-6">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-base">Select shipment to update</div>
                    <div className="flex items-center mb-4">
                        <div className="flex-1">
                            <SearchInput
                                value={searchTerm}
                                placeholder="Search for shipment name"
                                onChange={setSearchTerm}
                                showClearButton={true}
                                debounce={300}
                            />
                        </div>
                        <Button
                            variant="primary"
                            size="md"
                            className="ml-4"
                            onClick={() => setShowNewShipmentModal(true)}
                        >
                            New shipment
                        </Button>
                    </div>
                    {/* Shipments table with max height and scroll */}
                    <div className="border dark:border-white/10 rounded max-h-[40rem] overflow-y-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-slate-800">
                                    <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Created at</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Shipment name</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Location</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Boxes donated</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedFilteredShipments.map((s, idx) => (
                                    <tr key={s.id || idx} className="border-t dark:border-white/10 hover:bg-gray-50 dark:hover:bg-slate-800">
                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{s.createAt ? formatDate(s.createAt) : "-"}</td>
                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{s.name}</td>
                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{s.address || "-"}</td>
                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{s.orderedBoxUnit}</td>
                                        <td className="px-4 py-3">
                                            <Badge
                                                label={s.currentStutus}
                                                variant={s.currentStutus === "SHIPPED" ? "success" : "primary"}
                                                size="sm"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Pagination */}
                        <div className="flex justify-between items-center px-4 py-2 bg-white dark:bg-slate-900 border-t dark:border-white/10">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                {shipments.length === 0 ? 0 : ((currentPage - 1) * pageSize + 1)}-
                                {Math.min(currentPage * pageSize, shipments.length)} of {shipments.length}
                            </span>
                            <div>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    &lt;
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages || shipments.length === 0}
                                >
                                    &gt;
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Footer */}
                <div className="flex justify-end gap-2 px-8 py-6 border-t dark:border-white/10">
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        disabled={true}
                    >
                        Assign
                    </Button>
                </div>
            </div>
            {/* New Shipment Modal */}
            <NewShipmentModal
                show={showNewShipmentModal}
                onClose={() => setShowNewShipmentModal(false)}
                onSuccess={handleNewShipment}
                donations={donations}
            />
        </div>
    ,
    document.body
  );
};

export default DonationAssignModal;
